// useOhneFilterProbe — „Ohne Filter gäbe es doch etwas" (PO 21.09.2026,
// aus `VorgaengeMo.tsx` herausgezogen am 02.10.2026 für die Kontaktliste).
//
// User vergessen oft, dass sie Filter gesetzt haben. Bleibt eine Liste trotz
// Suche leer, liegt es meist an einem Filter. Statt den Anwender raten zu
// lassen, zählt die Liste einmal ohne Filter nach; die Oberfläche bietet dann
// den Weg an (`<OhneFilterHinweis>`).

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Lohnt die Nachzählung ohne Filter?
 *
 * Nur wenn die Liste FERTIG geladen und leer ist und überhaupt ein Filter
 * wirkt. Während des Ladens ist sie immer leer — dann zu zählen hieße, bei
 * jedem Tastendruck in der Suche eine zweite Abfrage loszuschicken und
 * zwischendurch einen Hinweis aufblitzen zu lassen, der gleich wieder falsch
 * ist. Ohne aktiven Filter gäbe es nichts anzubieten: Dann ist die Liste
 * wirklich leer, und „ohne Filter suchen" wäre dieselbe Abfrage.
 */
export function lohntOhneFilterProbe(args: { treffer: number; laedt: boolean; filterAnzahl: number }): boolean {
  return !args.laedt && args.treffer === 0 && args.filterAnzahl > 0;
}

export type OhneFilterProbeArgs = {
  /** Zeilen, die die Liste mit Filtern gerade zeigt. */
  treffer: number;
  laedt: boolean;
  /** Wie viele Filter wirken (0 ⇒ keine Probe). */
  filterAnzahl: number;
  /**
   * Zählt ohne Filter (sonst dieselbe Abfrage: Suche, Bereich, Sortierung).
   * `limit: 1` genügt — gebraucht wird nur die Gesamtzahl.
   */
  zaehlen: (signal: AbortSignal) => Promise<number | null | undefined>;
  /** Ändert er sich, wird neu gezählt (z. B. Suche, Bereich, Sortierung). */
  schluessel: string;
  /** Wartezeit vor der Probe, kürzer als die Entprellung der Suche. */
  verzoegerungMs?: number;
};

export function useOhneFilterProbe({
  treffer,
  laedt,
  filterAnzahl,
  zaehlen,
  schluessel,
  verzoegerungMs = 250,
}: OhneFilterProbeArgs): { anzahl: number | null; verwerfen: () => void } {
  const [anzahl, setAnzahl] = useState<number | null>(null);
  // Die Zählfunktion ist meist eine Inline-Closure — über eine Ref, damit nicht
  // jeder Render die Probe neu startet.
  const zaehlenRef = useRef(zaehlen);
  zaehlenRef.current = zaehlen;

  const probeLohnt = lohntOhneFilterProbe({ treffer, laedt, filterAnzahl });
  useEffect(() => {
    if (!probeLohnt) {
      setAnzahl(null);
      return;
    }
    const ctrl = new AbortController();
    /**
     * KURZ WARTEN, statt sofort zu fragen — sonst zahlt jeder Aufruf der
     * Seite eine zweite volle Zählung.
     *
     * Beim Aufbau ist `laedt` oft noch `false` und die Liste leer, ein Filter
     * aber schon an: Die Probe hielte sich also für fällig, BEVOR die Liste
     * überhaupt zu laden begonnen hat. Der `AbortController` bricht sie einen
     * Wimpernschlag später ab. Dasselbe bei jedem Tastendruck in der Suche,
     * solange die Liste schon leer ist.
     */
    const timer = setTimeout(() => {
      zaehlenRef
        .current(ctrl.signal)
        .then((n) => {
          if (!ctrl.signal.aborted) setAnzahl(n ?? null);
        })
        .catch(() => {
          // Still: Der Hinweis ist ein Angebot, kein Ergebnis. Eine
          // Fehlermeldung für eine Nebenabfrage wäre nur Lärm.
          if (!ctrl.signal.aborted) setAnzahl(null);
        });
    }, verzoegerungMs);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [probeLohnt, schluessel, verzoegerungMs]);

  const verwerfen = useCallback(() => setAnzahl(null), []);
  return { anzahl, verwerfen };
}
